import { HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { GenericService, ToolsService, UserAccessRole, UserRightsService } from '@huge-monorepo/portal/services';
import { Endpoints } from '@huge-monorepo/portal/config';
import { ICustomerTeltonikaService, ITeltonikaService, TeltonikaDeviceLANIPs, TeltonikaDevicesWANData, TeltonikaDeviceWANUsage, TeltonikaPingFromRouterData, TeltonikaSNMPAgentSettings, TeltonikaSNMPGeneralSettings, TeltonikaSpeedtestOption, TeltonikaTracerouteData } from '@huge-monorepo/shared/interfaces';
import { Observable, delay, map, of } from 'rxjs';
import { TeltonikaDevice } from '@huge-monorepo/shared/interfaces';
import { TeltonikaDataUsagePerSIM } from '@huge-monorepo/shared/interfaces';
import { TeltonikaLocation } from '@huge-monorepo/shared/interfaces';
import { TeltonikaDashboardData } from '@huge-monorepo/shared/interfaces';
import { TeltonikaTaskData } from '@huge-monorepo/shared/interfaces';
import { TeltonikaPortInfo } from '@huge-monorepo/shared/interfaces';
import { TeltonikaDataUsageEntry } from '@huge-monorepo/shared/interfaces';
import { TeltonikaLog } from '@huge-monorepo/shared/interfaces';
import { TeltonikaErrorLog } from '@huge-monorepo/shared/interfaces';
import { TeltonikaCheckInPayload } from '@huge-monorepo/shared/interfaces';
import { TeltonikaIPs } from '@huge-monorepo/shared/interfaces';
import { TeltonikaPingData } from '@huge-monorepo/shared/interfaces';
import { Tag, TeltonikaFilters } from '@huge-monorepo/shared/interfaces';
import { TeltonikaSelectAllData } from '@huge-monorepo/shared/interfaces';
import { TeltonikaSignalStrengthForPeriod } from '@huge-monorepo/shared/interfaces';
import {
  CreateTeltonikaSNMPUser,
  GetTeltonikaSNMPUser,
  UpdateTeltonikaSNMPUser,
} from '../../../../shared/src/interfaces/TeltonikaSNMPUser';

@Injectable({
  providedIn: 'root',
})
export class TeltonikaService implements OnDestroy, ICustomerTeltonikaService {

  private refreshInterval: NodeJS.Timer;
  private refreshPeriod: number = 300_000;
  private dashboardData: TeltonikaDashboardData;
  private selectedDevice: TeltonikaDevice;
  private portInfo: TeltonikaPortInfo;
  private dataUsage: TeltonikaDataUsageEntry[];
  private dataUsagePerSim: TeltonikaDataUsagePerSIM[];

  constructor(
    private genericService: GenericService,
    private endpoints: Endpoints,
    private tools: ToolsService,
    private authService: UserRightsService,
    private uar: UserAccessRole,
  ) {

  }

  ngOnDestroy(): void {
  }

  getDeviceByID(id: number, stored: boolean = false, opn?: boolean, timeout: number = 0): Observable<TeltonikaDevice> {
    // Uncomment this block if you want to use the cached selectedDevice
    // if (this.selectedDevice && this.selectedDevice.id == id) {
    //   return of({ ...this.selectedDevice });
    // }

    return this.genericService.POST(this.endpoints.GET_TELTONIKA_DEVICE_GENERAL_INFO(id, opn), {
      CDRDeviceInfo: stored,
      GeneralInfo: {
        Firmware: !stored,
        IMEI: !stored,
        MAC: !stored,
        Serial: !stored
      },
      LocationInfo: {
        LocationInfo: !stored
      },
      NetworkInfo: {
        ActiveSim: !stored,
        MobileConnectionType: !stored,
        MobileStatus: !stored,
        NeighbouringSims: !stored,
        PortWANIP: !stored,
        ServiceProvider: !stored,
        SignalStrength: !stored,
        SimNum: !stored,
        SimWANIP: !stored
      },
      SpecificInfo: {
        CPUUsage: !stored,
        ModemInfo: !stored,
        RAMUsage: !stored,
        Temperature: !stored,
        Uptime: !stored
      }
    })
      .pipe(
        delay(timeout),
        map((response: any) => {
          this.selectedDevice = response.body as TeltonikaDevice;
          this.selectedDevice.id = id;
          return { ...this.selectedDevice };
        })
      );
  }

  getDeviceByIDSplit(id: number, opn?: boolean): Observable<TeltonikaDevice>[] {
    return [this.endpoints.GET_TELTONIKA_DEVICE_GENERAL_INFO(id, opn), this.endpoints.GET_TELTONIKA_DEVICE_GENERAL_INFO(id, opn)].map((url, index) =>
      this.genericService.POST(url,
        {
          CDRDeviceInfo: (index === 0),
          GeneralInfo: {
            Firmware: !(index === 0),
            IMEI: !(index === 0),
            MAC: !(index === 0),
            Serial: !(index === 0)
          },
          LocationInfo: {
            LocationInfo: !(index === 0)
          },
          NetworkInfo: {
            ActiveSim: !(index === 0),
            MobileConnectionType: !(index === 0),
            MobileStatus: !(index === 0),
            NeighbouringSims: !(index === 0),
            PortWANIP: !(index === 0),
            ServiceProvider: !(index === 0),
            SignalStrength: !(index === 0),
            SimNum: !(index === 0),
            SimWANIP: !(index === 0)
          },
          SpecificInfo: {
            CPUUsage: !(index === 0),
            ModemInfo: !(index === 0),
            RAMUsage: !(index === 0),
            Temperature: !(index === 0),
            Uptime: !(index === 0)
          }
        }
      ).pipe(map(data => {
        return data as TeltonikaDevice;
      }))
    );
  }

  getDashboardData(opn: boolean = false): Observable<TeltonikaDashboardData> {
    // if (this.dashboardData != null) {
    //   return of({ ...this.dashboardData });
    // }

    return this.genericService.GET(this.endpoints.GET_TELTONIKA_DASHBOARD(this.authService.GetGroupID(), opn)).pipe(map((val: any) => {
      let response = { ...val.body } as TeltonikaDashboardData;


      response.LastNonCachedResponse = new Date((+val.headers.get("x-last-non-cached-response")) * 1000);
      return response;
    }));
  }

  getTeltonikaTableTasks(search?: string): Observable<TeltonikaTaskData[]> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_TASKS(search)).pipe(map((val: any) => {
      return val.body as TeltonikaTaskData[];
    }));
  }

  getTeltonikaTask(taskID: number): Observable<TeltonikaTaskData> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_TASK(taskID)).pipe(map((val: any) => {
      return val.body as TeltonikaTaskData;
    }));
  }

  startRefreshTimer(): void {
    this.refreshInterval = setInterval(() => {
      this.dashboardData = null;
      this.selectedDevice = null;
    }, this.refreshPeriod);
  }

  getPortInfo(id: number): Observable<TeltonikaPortInfo> {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_DEVICE_PORT_INFO(id), {
      ConnectedDevices: true,
      Ports: true
    }).pipe(map((data: any) => {
      this.portInfo = data.body;
      return this.portInfo;
    }));
  }

  getDataUsage(id: number, startDate: Date, endDate: Date, Monthly: boolean = false): Observable<any[]> {
    const data = {
      Start: this.tools.FormatStartDate(startDate.toISOString()),
      End: this.tools.FormatEndDate(endDate.toISOString()),
      DeviceID: +id,
      PerMonth: Monthly,
      TeltonikaDevice: true,
    };
    return this.genericService.POST(this.endpoints.GET_DEVICE_USAGE_OVER_TIME, data).pipe(map((data: any) => {
      this.dataUsage = data.body;
      return this.dataUsage;
    }));
  }

  getSplitDataUsage(id: number, startDate: Date, endDate: Date, Monthly: boolean = false): Observable<any[]> {
    const data = {
      Start: this.tools.FormatStartDate(startDate.toISOString()),
      End: this.tools.FormatEndDate(endDate.toISOString()),
      DeviceID: +id,
      TeltonikaDevice: true,
    };
    return this.genericService.POST(this.endpoints.GET_DEVICE_USAGE_SPLIT_OVER_TIME, data).pipe(map((data: any) => {
      this.dataUsage = data.body;
      return this.dataUsage;
    }));
  }

  getDataUsagePerSim(id: number, startDate: Date, endDate: Date, simNumber: number): Observable<TeltonikaDataUsagePerSIM[]> {
    const data = {
      From: Math.trunc(startDate.getTime() / 1000),
      To: Math.trunc(endDate.getTime() / 1000),
      SimSlot: simNumber,
      Interface: 'wwan0',
      Modem: 1,
    };
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_DEVICE_USAGE_PER_SIM(id), data).pipe(map((data: any) => {
      this.dataUsagePerSim = data.body;
      return this.dataUsagePerSim;
    }));
  }

  rebootDevices(ids: number[]): Observable<any> {
    return this.genericService.POST(this.endpoints.REBOOT_TELTONIKA_DEVICES, { DeviceIDs: ids, ModemOnly: false }).pipe(map((data: any) => { return data; }));
  }

  generateFile(id: number, type: string): Observable<any> {
    return this.genericService.download(this.endpoints.GENERATE_TELTONIKA_DEVICE_TROUBLESHOOTING_FILE(id, type), null, "get").pipe(map(() => { }));
  }

  downloadTeltonikaFile(id: number): Observable<any> {
    return this.genericService.download(this.endpoints.DOWNLOAD_TELTONIKA_DEVICE_FILE(id), null, "get").pipe(map(() => { }));
  }

  getDeviceLogs(deviceID: number, types: string[], limit: number = 5, includeTaskLogs: boolean = false): Observable<TeltonikaLog[]> {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_DEVICE_LOGS(deviceID, limit), { Types: types, IncludeTaskLogs: includeTaskLogs }).pipe(map((data: any) => {
      if (data == null || data.body == null) {
        return null;
      }
      const body = data.body;
      return body.map(element => {
        if (element['Timestamp']) {
          element['Timestamp'] = this.tools.FormatDateTimeFromDateSwedish(this.tools.getDateFromString(element['Timestamp']));
        }
        return element as TeltonikaLog[];
      });
    }));
  }

  getDeviceErrorLogs(deviceID: number, limit: number = 5): Observable<TeltonikaErrorLog[]> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_DEVICE_ERROR_LOGS(deviceID, limit)).pipe(map((data: any) => {
      if (data == null || data.body == null) {
        return null;
      }
      const body = data.body;
      return body.map(element => {
        if (element['Timestamp']) {
          element['Timestamp'] = this.tools.FormatDateTimeFromDateSwedish(this.tools.getDateFromString(element['Timestamp']));
        }
        return element as TeltonikaErrorLog[];
      });
    }));
  }

  startTeltonikaTunneling(id: number, protocol: number, ip: string): Observable<string> {
    return this.genericService.POST(this.endpoints.START_TELTONIKA_TUNNELING(id), { Protocol: protocol, IP: ip }).pipe(map((data: any) => { 
      if (data == null || data.body == null) {
        return null;
      }
      return data.body.Address; }));
  }

  getReportingData(ids: number[], startDate: Date, endDate: Date, viewByDevice: boolean = true): Observable<any[]> {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_REPORTING(true, null), { IDs: ids, Start: this.tools.FormatStartDate(startDate.toISOString()), End: this.tools.FormatEndDate(endDate.toISOString()), SelectDevices: viewByDevice }).pipe(map((data: any) => {
      return data;
    }));
  }

  exportReportingData(ids: number[], startDate: Date, endDate: Date, selectAllInfo: TeltonikaSelectAllData | null = null): Observable<any[]> {
    return this.genericService.download(this.endpoints.GET_TELTONIKA_REPORTING(false, null), { IDs: ids, Start: this.tools.FormatStartDate(startDate.toISOString()), End: this.tools.FormatEndDate(endDate.toISOString()), ...selectAllInfo }).pipe(map((data: any) => {
      return data;
    }));
  }

  getDeviceUsageForPeriod(id: number, startDate: Date, endDate: Date, simSlot: number, modem: number, interfaceName: string = "wwan0") {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_SIM_USAGE_FOR_PERIOD(id), {
      Start: Math.floor(startDate.getTime() / 1000),
      End: Math.floor(endDate.getTime() / 1000),
      SimSlot: simSlot,
      InterfaceName: interfaceName,
      Modem: modem
    }).pipe(map((data: any) => {
      return data;
    }));
  }

  getDeviceCheckInPayloads(deviceID: number): Observable<TeltonikaCheckInPayload> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_CHECK_IN_PAYLOAD(deviceID)).pipe(map((data: any) => {
      if (data == null || data.body == null) {
        return null;
      }
      data.body.StandardCheckInTimestamp = this.tools.FormatDateTimeFromDateSwedish(this.tools.getDateFromString(data.body.StandardCheckInTimestamp));
      return data.body as TeltonikaCheckInPayload;
    }));
  }

  getDeviceIPs(deviceID: number): Observable<TeltonikaIPs> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_DEVICE_IPS(deviceID)).pipe(map((data: any) => {
      return data.body as TeltonikaIPs;
    }));
  }

  getDeviceLocations(deviceID: number, locationType?: string, startDate?: string, endDate?: string): Observable<TeltonikaLocation[]> {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_DEVICE_LOCATIONS(deviceID), { LocationType: locationType, Start: startDate, End: endDate }).pipe(map((data: any) => {
      if (data == null) {
        return null;
      }
      return data.body as TeltonikaLocation[];
    }));
  }

  getFilters(filter?: string, model?: string, tagID?: number, status?: string, signalStrength?: string): Observable<{ Firmwares: string[], Models: string[], Tags: Tag[], Statuses: string[], SignalStrength: string[] }> {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_FILTERS, { Firmware: filter, Model: model, Tag: tagID, Status: status }).pipe(map((data: any) => {
      return data.body as TeltonikaFilters;
    }));
  }

  getTeltonikaRemoteAccessWebsocketURL(id: number, ip: string, rows: number, cols: number, username: string, password: string): string {
    return this.endpoints.OPEN_TELTONIKA_REMOTE_ACCESS_WEBSOCKET(id, encodeURIComponent(ip), encodeURIComponent(this.authService.GetToken()), rows, cols, encodeURIComponent(username), encodeURIComponent(password));
  }

  pingDevice(deviceID: number) {
    return this.genericService.GET(this.endpoints.PING_TELTONIKA_DEVICE(deviceID)).pipe(map((data: any) => {
      return data as TeltonikaPingData;
    }));
  }

  getActionRequiredEmails(): Observable<HttpResponse<{ Emails: string[] }>> {
    return this.genericService.GET(this.endpoints.ACTION_REQUIRED_EMAILS).pipe(map((data: any) => {
      return data;
    }));
  }

  sendSMS(id: number, message: string): Observable<any> {
    return this.genericService.POST(this.endpoints.SEND_TELTONIKA_SMS(id), { Message: message }).pipe(map((data: any) => {
      return data.body;
    }));
  }

  switchSim(deviceID: number): Observable<HttpResponse<any>> {
    return this.genericService.GET(this.endpoints.SWITCH_TELTONIKA_DEVICE_SIM(deviceID)).pipe(map((data: any) => {
      return data;
    }));
  }

  getTeltonikaDevices(search?: string, count?: number, offset?: number, desc?: boolean, orderBy?: string, nullTags?: boolean, tagID?: number): Observable<any[]> {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_DEVICES(search, count, offset, desc, orderBy), { TeltonikaTagIsNull: nullTags, TeltonikaTagID: tagID }).pipe(map((data: any) => {
      return data.body;
    }));
  }

  getTag(id: number): Observable<any> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_TAG(id)).pipe(map((data: any) => {
      return data as Tag;
    }));
  }

  openTeltonikaTaskPollingWebsocket(sessionGuid: string): WebSocket {
    return new WebSocket(this.endpoints.OPEN_TELTONIKA_TASK_POLLING_WEBSOCKET(encodeURIComponent(this.authService.GetToken()), encodeURIComponent(sessionGuid)))
  }

  getTeltonikaSignalStrengthForPeriod(id: number, startDate: Date, endDate: Date): Observable<TeltonikaSignalStrengthForPeriod[]> {
    const data = {
      Start: this.tools.FormatStartDate(startDate.toISOString()),
      End: this.tools.FormatEndDate(endDate.toISOString()),
      DeviceID: id,
    };
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_SIGNAL_STRENGTH_FOR_PERIOD, data).pipe(map((data: any) => {
      return data.body as TeltonikaSignalStrengthForPeriod[];
    }));
  }

  isTeltonikaAdmin(): boolean {
    return this.uar.IsTeltonikaAdmin();
  }

  isTeltonikaRemoteAccess(): boolean {
    return this.uar.IsTeltonikaRemoteAccess();
  }

  downloadUpdateTeltonikaDeviceTemplate(): Observable<any> {
    return this.genericService.download(this.endpoints.GET_TELTONIKA_UPDATE_DEVICE_TEMPLATE, null, "get").pipe(map(() => { }));
  }

  downloadUpdateTeltonikaTagTemplate(): Observable<any> {
    return this.genericService.download(this.endpoints.GET_TELTONIKA_UPDATE_TAG_TEMPLATE, null, "get").pipe(map(() => { }));
  }

  generateTeltonikaConnectedDevicesReport() {
    return this.genericService.download(this.endpoints.GET_TELTONIKA_CONNECTED_DEVICES_REPORT, null, "get").pipe(map(() => { }));
  }

  updateTeltonikaSNMPAgentConfig(id: number, settings: TeltonikaSNMPAgentSettings): Observable<any> {
    return this.genericService.PUT(this.endpoints.UPDATE_TELTONIKA_SNMP_AGENT_CONFIG(id), settings).pipe(map((data: any) => {
      return data;
    }));
  }

  getTeltonikaSNMPAgentConfig(id: number): Observable<TeltonikaSNMPAgentSettings> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_SNMP_AGENT_CONFIG(id)).pipe(map((data: any) => {
      return data.body as TeltonikaSNMPAgentSettings;
    }));
  }

  updateTeltonikaSNMPGeneralConfig(id: number, snmpGeneralConfig: TeltonikaSNMPGeneralSettings): Observable<any> {
    return this.genericService.PUT(this.endpoints.UPDATE_TELTONIKA_SNMP_GENERAL_CONFIG(id), snmpGeneralConfig).pipe(map((data: any) => {
      return data;
    }));
  }

  getTeltonikaSNMPGeneralConfig(id: number): Observable<TeltonikaSNMPGeneralSettings> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_SNMP_GENERAL_CONFIG(id)).pipe(map((data: any) => {
      return data.body as TeltonikaSNMPGeneralSettings;
    }));
  }

  createTeltonikaSNMPUser(id: number, snmpUser: CreateTeltonikaSNMPUser): Observable<any> {
    return this.genericService.POST(this.endpoints.CREATE_TELTONIKA_SNMP_USER(id), snmpUser).pipe(map((data: any) => {
      return data;
    }));
  }

  updateTeltonikaSNMPUser(id: number, snmpUser: UpdateTeltonikaSNMPUser): Observable<any> {
    return this.genericService.PUT(this.endpoints.UPDATE_TELTONIKA_SNMP_USER(id), snmpUser).pipe(map((data: any) => {
      return data;
    }));
  }

  getTeltonikaSNMPUsers(id: number): Observable<GetTeltonikaSNMPUser> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_SNMP_USERS(id)).pipe(map((data: any) => {
      return data.body as GetTeltonikaSNMPUser;
    }));
  }

  getTeltonikaDeviceLANIPs(id: number): Observable<TeltonikaDeviceLANIPs> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_DEVICE_LAN_IPS(id)).pipe(map((data: any) => {
      return data.body as TeltonikaDeviceLANIPs;
    }));
  }

  getTeltonikaDeviceWANData(id: number): Observable<TeltonikaDevicesWANData> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_DEVICE_WAN_DATA(id)).pipe(map((data: any) => {
      return data.body as TeltonikaDevicesWANData;
    }));
  }

  getWANReportingData(ids: number[], startDate: Date, endDate: Date, viewByDevice: boolean = true): Observable<any[]> {
    return this.genericService.POST(this.endpoints.GET_TELTONIKA_WAN_REPORTING(true, null), { IDs: ids, Start: this.tools.FormatStartDate(startDate.toISOString()), End: this.tools.FormatEndDate(endDate.toISOString()), SelectDevices: viewByDevice }).pipe(map((data: any) => {
      return data.body;
    }));
  }

  exportWANReportingData(ids: number[], startDate: Date, endDate: Date, selectAllInfo: TeltonikaSelectAllData | null = null): Observable<any[]> {
    return this.genericService.download(this.endpoints.GET_TELTONIKA_WAN_REPORTING(false, null), { IDs: ids, Start: this.tools.FormatStartDate(startDate.toISOString()), End: this.tools.FormatEndDate(endDate.toISOString()), ...selectAllInfo }).pipe(map((data: any) => {
      return data.body;
    }));
  }

  getTeltonikaDeviceWANUsage(id: number, startDate: Date, endDate: Date) {
    return this.genericService.POST(this.endpoints.TELTONIKA_DEVICE_WAN_USAGE(id), {
      StartDate: this.tools.FormatStartDate(startDate.toISOString()),
      EndDate: this.tools.FormatEndDate(endDate.toISOString())
    }).pipe(map((data: any) => {
      if (data != null) {
        return data.body.Usages as { [date: string]: TeltonikaDeviceWANUsage };
      }
      return [];
    }));
  }

  pingFromDevice(deviceID: number, address: string) {
    return this.genericService.POST(this.endpoints.PING_FROM_TELTONIKA_DEVICE(deviceID), { IP: address }).pipe(map((data: any) => {
      return data.body as TeltonikaPingFromRouterData;
    }));
  }

  tracerouteFromDevice(deviceID: number, address: string) {
    return this.genericService.POST(this.endpoints.TRACEROUTE_FROM_TELTONIKA_DEVICE(deviceID), { Host: address }).pipe(map((data: any) => {
      return data.body as TeltonikaTracerouteData;
    }));
  }

  getSpeedtestOptions(deviceID: number): Observable<{ options: TeltonikaSpeedtestOption[] }> {
    return this.genericService.GET(this.endpoints.GET_TELTONIKA_SPEEDTEST_OPTIONS(deviceID)).pipe(map((data: any) => {
      return data.body as { options: TeltonikaSpeedtestOption[] };
    }));
  }

  runSpeedtest(deviceID: number, url: string): WebSocket {
    return new WebSocket(this.endpoints.RUN_TELTONIKA_SPEEDTEST(deviceID, encodeURIComponent(this.authService.GetToken()), encodeURIComponent(url)));
  }
}
